import React from 'react'

import environment from 'environment'

import RequestService from 'services/request.service'
import ToastService from 'services/toast.service'

import { store } from 'app-redux/store'
import { CardType } from 'types/deck'
import { PRICE_SOURCE } from 'types/active'
import { Option } from 'components/elements/ArchidektDropdown/types'

import PhatButton from 'components/formElements/PhatButton'
import Icon from 'components/elements/Icon'

import { getAffiliateParams } from 'utils/Price'
import { generateShoppingResults } from 'utils/Shopping'

import styles from './buyCardsLink.module.scss'

type Props = {
  cards: CardType[]
  priceSource: number
  linkedFrom: string
  isSellLink?: boolean
  label?: string
  children?: React.ReactNode
  isDisabled?: boolean
}

export const BuyCardsLink = ({
  cards,
  priceSource,
  linkedFrom,
  label = '',
  children = null,
  isSellLink = false,
  isDisabled = false,
}: Props) => {
  const {
    ckprice,
    tcgprice,
    chprice,
    tcgstring,
    ckstring,
    chelements,
    scgstring,
    scgprice,
    mpstring,
    mpprice,
    tcgLandPrice,
    tcgLandString,
    ckSellList,
  } = generateShoppingResults(cards.filter(card => !card?.customCard))

  const affiliateParams = getAffiliateParams(linkedFrom)

  // prettier-ignore
  const tcgurl = `https://partner.tcgplayer.com/c/4913290/1830156/21018?partnerpropertyid=${environment.tcgPropertyId}&${affiliateParams.tcg}&u=${encodeURIComponent(`https://store.tcgplayer.com/massentry`)}`
  const ckurl = `https://www.cardkingdom.com/builder?${affiliateParams.ck}`
  const ckSellUrl = `https://www.cardkingdom.com/sellcart/partner_import?`
  const churl = `https://www.cardhoarder.com/decks/upload?${affiliateParams.mtgo}`
  const tcgLandUrl = `https://www.tcg.land/multisearch#/magic-the-gathering?format=archidekt&separator=|&lines=${tcgLandString}&utm_medium=affiliate&utm_source=${environment.tcgLandAffiliateUrlParam}&via=archidekt-${linkedFrom}`
  const scgUrl = scgstring

  // prettier-ignore
  const mpurl = `https://manapool.com/add-deck?${environment.manapoolAffiliateUrlParams}&deck=${urlSafeBase64Encode(mpstring)}&${affiliateParams.mp}`

  let url = ''
  let searchString = ''
  let childElements = undefined
  let price = 0.0
  let partner = ''

  if (priceSource === PRICE_SOURCE.CK) {
    url = isSellLink ? ckSellUrl : ckurl
    searchString = ckstring
    childElements = undefined
    price = ckprice
    partner = isSellLink ? 'EDHRECSell' : 'archidekt'
  }

  if (priceSource === PRICE_SOURCE.TCG) {
    url = `${tcgurl}?c=${encodeURIComponent(tcgstring)}`
    searchString = tcgstring
    childElements = null
    price = tcgprice
    partner = 'Archidekt'
  }

  if (priceSource === PRICE_SOURCE.MTGO) {
    url = churl
    searchString = ''
    childElements = chelements
    price = chprice
    partner = 'archidekt'
  }

  if (priceSource === PRICE_SOURCE.SCG) {
    searchString = scgUrl
    price = scgprice
  }

  if (priceSource === PRICE_SOURCE.MP) {
    url = mpurl
    price = mpprice
  }

  if (priceSource === PRICE_SOURCE.TCGLAND) {
    url = tcgLandUrl
    price = tcgLandPrice
  }

  if (priceSource === PRICE_SOURCE.JE) {
    searchString = mpstring
  }

  if (priceSource === PRICE_SOURCE.CS) {
    searchString = mpstring
  }

  if (priceSource === PRICE_SOURCE.CT) {
    searchString = mpstring.replace(/\[/g, '(').replace(/]/g, ')') // Needs () for set codes rather than []
  }

  if (!url && priceSource !== PRICE_SOURCE.SCG && priceSource !== PRICE_SOURCE.JE && priceSource !== PRICE_SOURCE.CT && priceSource !== PRICE_SOURCE.CS)
    return null

  const renderedChildren = children || (
    <button type="submit">
      <Icon name={isSellLink ? 'dolly' : 'shopping cart'} /> {label || 'Buy selected cards'}
    </button>
  )

  return (
    <ExternalFormButtonOrLink
      asLink={priceSource === PRICE_SOURCE.TCG || priceSource === PRICE_SOURCE.MP}
      url={url}
      searchString={searchString}
      price={price}
      partner={partner}
      chelements={childElements}
      scgLink={priceSource === PRICE_SOURCE.SCG}
      jeLink={priceSource === PRICE_SOURCE.JE}
      ctLink={priceSource === PRICE_SOURCE.CT}
      csLink={priceSource === PRICE_SOURCE.CS}
      ckSellList={ckSellList}
      linkedFrom={linkedFrom}
      isDisabled={isDisabled}
      isSellLink={isSellLink}
    >

      {renderedChildren}
    </ExternalFormButtonOrLink>
  )
}

export default BuyCardsLink

type ExternalFormButtonOrLinkProps = {
  url: string
  searchString: string
  price: number
  partner: string
  text?: string
  color?: string
  icon?: string
  chelements?: any
  scgLink?: boolean
  jeLink?: boolean
  ctLink?: boolean
  csLink?: boolean
  children?: React.ReactNode
  onClick?: () => void
  asLink?: boolean
  ckSellList?: { id: string; qty: number }[]
  linkedFrom?: string
  isDisabled?: boolean
  isSellLink?: boolean
}

const ExternalFormButtonOrLink = ({
  url,
  searchString,
  price,
  color,
  icon,
  partner,
  text,
  chelements,
  scgLink,
  jeLink,
  ctLink,
  csLink,
  children = null,
  onClick,
  asLink = false,
  ckSellList,
  linkedFrom = 'generic',
  isDisabled = false,
  isSellLink = false,
}: ExternalFormButtonOrLinkProps) => {
  const [loading, setLoading] = React.useState(false)

  const affiliateParams = getAffiliateParams(linkedFrom)

  const handleFetchSCGUrl = async () => {
    setLoading(true)

    RequestService.post<{ url: string }>('/api/cards/bulk-scg-purchase/', { data: searchString })
      .then(res => {
        window
          .open(
            `https://goto.starcitygames.com/c/4913290/3052179/37198?${affiliateParams.scg}&u=${encodeURIComponent(res.url)}`,
            '_blank',
          )
          ?.focus()
      })
      .catch(() => ToastService.create('Unable to fetch SCG URL', linkedFrom, 'error'))
      .finally(() => setLoading(false))
  }

  const handleFetchJEUrl = async () => {
    setLoading(true)
    RequestService.post<{ url: string }>('/api/cards/bulk-je-purchase/', { decklist: searchString })
      .then(res => {
        window.open(`${res.url}&${affiliateParams.je}`, '_blank') // requires an & since the url already has ?
      })
      .catch(() => ToastService.create("Unable to fetch Journey's End URL", linkedFrom, 'error'))
      .finally(() => setLoading(false))
  }

  const handleFetchCTUrl = async () => {
    setLoading(true)
    RequestService.post<{ url: string }>('/api/cards/bulk-card-trader-purchase/', { decklist: searchString })
      .then(res => {
        window.open(`${res.url}?${affiliateParams.ct}`, '_blank')
      })
      .catch(() => ToastService.create('Unable to fetch Card Trader URL', linkedFrom, 'error'))
      .finally(() => setLoading(false))
  }

  const handleFetchCSUrl = async () => {
    setLoading(true)
    RequestService.post<{ url: string }>('/api/cards/bulk-cs-export/', { decklist: searchString })
      .then(res => {
        window.open(`https://www.cardsphere.com${res.url}&${affiliateParams.cs}&action=${isSellLink ? 'sell' : 'buy'}`, '_blank')
      })
      .catch(() => ToastService.create('Unable to fetch Cardsphere URL', linkedFrom, 'error'))
      .finally(() => setLoading(false))
  }

  if (asLink)
    return (
      <a href={url} target="_blank">
        {children}

        {!children && (
          <PhatButton style={{ backgroundColor: color }} onClick={onClick}>
            {icon && <Icon className={icon} />}
            {text}
            {price}
          </PhatButton>
        )}
      </a>
    )

  if (scgLink && children)
    return React.cloneElement(children as any, { onClick: handleFetchSCGUrl, disabled: isDisabled || loading })
  if (jeLink && children)
    return React.cloneElement(children as any, { onClick: handleFetchJEUrl, disabled: isDisabled || loading })
  if (ctLink && children)
    return React.cloneElement(children as any, { onClick: handleFetchCTUrl, disabled: isDisabled || loading })
  if (csLink && children)
    return React.cloneElement(children as any, { onClick: handleFetchCSUrl, disabled: isDisabled || loading })

  return (
    <form method="post" action={url} target="_blank" style={{ width: '100%' }}>
      {chelements === undefined ? (
        <>
          <input type="hidden" name="c" value={searchString} />
          <input type="hidden" name="json" value={JSON.stringify({ contents: ckSellList })} />
          <input type="hidden" name="partner" value={partner} />
          <input type="hidden" name="po_origin" value={1} />
          <input type="hidden" name="partner_args" value="archidekt,buylist" />
        </>
      ) : (
        chelements
      )}

      {children}

      {!children && (
        <PhatButton style={{ backgroundColor: color }} onClick={onClick}>
          {icon && <Icon className={icon} />}
          {text}
          {price}
        </PhatButton>
      )}
    </form>
  )
}

export const buyFromDropdown = (
  cardIds: string[],
  priceSource: number,
  linkedFrom: string,
  label?: string,
  isSellLink?: boolean,
): Option => {
  const { cardMap } = store.getState().deck
  const cards = cardIds.map(cardId => cardMap[cardId]).filter(card => !card?.customCard)

  return buyOrSellLink(cards, priceSource, linkedFrom, label, isSellLink)
}

export const sellFromCollectionDropdown = (
  cardIds: string[],
  priceSource: number,
  linkedFrom: string,
  label?: string,
): Option => {
  const { collectionCards } = store.getState().collectionV2
  const cards = cardIds
    .map(cardId => {
      const collectionCard = collectionCards[cardId]

      return {
        ...collectionCard.card,
        qty: collectionCard.quantity,
      }
    })
    .filter(card => !card?.customCard)

  return buyOrSellLink(cards, priceSource, linkedFrom, label, true)
}

export const buyOrSellLink = (
  cards: CardType[],
  priceSource: number,
  linkedFrom: string,
  label?: string,
  isSellLink?: boolean,
): Option => {
  const customChild = (
    <BuyCardsLink
      cards={cards}
      priceSource={priceSource}
      linkedFrom={linkedFrom}
      label={label}
      isSellLink={isSellLink}
    />
  )

  return {
    type: 'custom',
    className: styles.option,
    customChild: customChild,
    hidden: !customChild || !cards.length,
  }
}

export const urlSafeBase64Encode = (str: string) =>
  btoa(unescape(encodeURIComponent(str)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
